'use client';

import { useState, useCallback, useEffect } from 'react';
import type { InteractionStorageAdapter } from '../../model/interaction/adapters';
import { INTERACTION_STORAGE_KEYS } from '../../constants/interaction/constants';

export interface UseOnboardingModalOptions {
  enabled: boolean;
  storage: InteractionStorageAdapter;
}

interface UseOnboardingModalReturn {
  /** True when the 20% auto-show roll succeeded and hasn't been shown yet */
  shouldAutoShow: boolean;
  /** Mark the auto-show as complete (persists to storage) */
  markAutoShowComplete: () => void;
}

/**
 * Controls the one-time auto-show onboarding modal with a 20% probability.
 * Manual open (click) is handled separately in the orchestrator — always allowed.
 */
export function useOnboardingModal({
  enabled,
  storage,
}: UseOnboardingModalOptions): UseOnboardingModalReturn {
  const [shouldAutoShow] = useState<boolean>(() => {
    if (!enabled) {
      return false;
    }

    const alreadyAutoShown = storage.getItem<boolean>(
      INTERACTION_STORAGE_KEYS.onboardingAutoShown,
      false,
    );

    if (alreadyAutoShown) {
      return false;
    }

    return Math.random() < 0.2;
  });

  // Persist the roll result to storage outside of the initializer
  // to avoid side effects in useState (breaks under React Strict Mode)
  useEffect(() => {
    if (shouldAutoShow) {
      storage.setItem(INTERACTION_STORAGE_KEYS.onboardingAutoShown, true);
    }
  }, [shouldAutoShow, storage]);

  const markAutoShowComplete = useCallback(() => {
    storage.setItem(INTERACTION_STORAGE_KEYS.onboardingAutoShown, true);
  }, [storage]);

  return {
    shouldAutoShow,
    markAutoShowComplete,
  };
}
